import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router'
import styles from './FollowsCard.scss'
import { UnderlineSelector } from 'common/UnderlineSelector'
import { ProfileImg } from 'common/ProfileImg'
import { Icon } from 'common/Icon'
import { IntroWidget } from 'common/IntroWidget'
import { CircleSpinner } from 'common/spinners'
import { Card } from 'common/styleguide/Card'
import { PopoverLinkMenu, PopoverLinkMenuItem } from 'common/styleguide/PopoverLinkMenu'
import { RichListItemBadge as Badge } from 'common/styleguide/RichListItem'
import { followMenuItems, messages as friendInteractionMessages } from 'common/FriendInteractionsButtons'
import { useFormatMessage, Msg, commonNouns } from 'lib/localization'
import { urls } from 'lib/urls'
import { c } from 'lib/textUtils'
import { useHover } from 'lib/hooks/useHover'
import { followActions } from 'data/resources/follows'
import { userSelectors } from 'data/resources/users'
import Immutable, { OrderedMap } from 'immutable'
import { defineMessages } from 'react-intl'
import p from 'prop-types'
import cx from 'classnames'

const messages = defineMessages({
  nFollowers: {
    id: 'FollowsCard.nFollowers',
    defaultMessage: '{count, plural, =0 {followers} one {follower} other {followers}}'
  },
  nFollowing: {
    id: 'FollowsCard.nFollowing',
    defaultMessage: '{count, plural, =0 {following} one {following} other {following}}'
  },
  noFollowers: {
    id: 'FollowsCard.noFollowers',
    defaultMessage: 'This user has no followers'
  },
  noFollowing: {
    id: 'FollowsCard.noFollowing',
    defaultMessage: 'This user is not following anyone'
  },
  userNoFollowers: {
    id: 'FollowsCard.userNoFollowers',
    defaultMessage: 'Friends, family, or fans from anywhere in the world can follow along with all of your activities'
  },
  userNoFollowing: {
    id: 'FollowsCard.userNoFollowing',
    defaultMessage: 'Follow other users to view their activities, see their routes, and get inspired for your next great ride'
  }
})

const selectorOpts = (nFollowers, nFollowing) => OrderedMap({
  followers: nFollowers,
  following: nFollowing
})

const otherSelection = selection => selection === 'following'
  ? 'followers'
  : 'following'

// NOTE: defaultTo will cause the FollowsCard to prefer to show that category
// of user on first render, but if defaultTo === 'following' and `following` is
// an empty set, but `follows` is not, then the card will default to follows
export const FollowsCard = ({
  defaultTo = 'following',
  user,
  currentUser,
  interactive,
  friendBadge,
  ...rest
}) => {
  const { following, followers } = rest
  const determineInitialSelection = () => {
    return rest[defaultTo].size === 0 && rest[otherSelection(defaultTo)].size > 0
      ? otherSelection(defaultTo)
      : defaultTo
  }
  const fm = useFormatMessage()
  const [selected, setSelected] = useState(determineInitialSelection())

  useEffect(() => {
    setSelected(determineInitialSelection())
  }, [user.id])

  let options = selectorOpts(
    `${followers.size} ${c(fm(messages.nFollowers, { count: followers.size }))}`,
    `${following.size} ${c(fm(messages.nFollowing, { count: following.size }))}`
  )
  if (defaultTo === 'following' ||
    (followers.size === 0 && following.size > 0)) {
    options = options.reverse()
  }
  const display = selected === 'following'
    ? following
    : followers

  let friendsCount
  if (interactive && selected === 'following') {
    friendsCount = following.filter(f => f.level === 'friend').size
  }
  return (
    <Card noPadding>
      <UnderlineSelector
        className={styles.headingFilter}
        styles={{ option: styles.option, selected: styles.selected }}
        options={options}
        selected={selected}
        onSelectionChanged={s => setSelected(s)} />
      {display.size === 0
        ? <IntroWidget i='md-people' className={styles.empty}>
          {currentUser.id === user.id
            ? <Msg m={selected === 'following' ? messages.userNoFollowing : messages.userNoFollowers} />
            : <Msg m={selected === 'following' ? messages.noFollowing : messages.noFollowers} />}
        </IntroWidget>
        : <div className={styles.userList}>
          {interactive && selected === 'following' && <div className={styles.numFriends}>
            {friendsCount} <Msg m={commonNouns.friends} values={{ count: friendsCount }} />
          </div>}
          {display.toIndexedSeq().map(follow => <MiniUser key={`${follow.userId}${follow.itemId}`} follow={follow} selected={selected} interactive={interactive} friendBadge={friendBadge} />)}
        </div>}
    </Card>
  )
}

const _MiniUser = ({
  follow,
  currentUser,
  interactive,
  friendBadge,
  selected,
  updateFollow,
  followUser,
  unfollowUser,
  findUser
}) => {
  const [hoverRef, isHovered] = useHover()
  const [show, setShow] = useState(false)
  const [inflight, setInflight] = useState(false)
  const triggerRef = useRef(null)
  const fm = useFormatMessage()
  const inflightAction = action => () => {
    setInflight(true)
    Promise.resolve(action()).then(() => setInflight(false))
  }
  const user = selected === 'following'
    ? findUser(follow.itemId)
    : findUser(follow.userId)

  let actions
  if (selected === 'following') {
    actions = follow.level === 'friend'
      ? <>
        <PopoverLinkMenuItem onClick={() => updateFollow(follow.id, 'follow')}>{fm(friendInteractionMessages.removeFriend)}</PopoverLinkMenuItem>
        <PopoverLinkMenuItem onClick={() => unfollowUser(follow.id)}>{fm(friendInteractionMessages.unfollow)}</PopoverLinkMenuItem>
      </>
      : <>
        <PopoverLinkMenuItem onClick={() => updateFollow(follow.id, 'friend')}>{fm(friendInteractionMessages.addToFriends)}</PopoverLinkMenuItem>
        <PopoverLinkMenuItem onClick={() => unfollowUser(follow.id)}>{fm(friendInteractionMessages.unfollow)}</PopoverLinkMenuItem>
    </>
  } else if (selected === 'followers') {
    const menuItems = followMenuItems({ inflightAction, follow, followUser, updateFollow, user, currentUser, fm })
    actions = menuItems.keySeq().map(k => (
      <PopoverLinkMenuItem key={k} onClick={() => { setShow(false); menuItems.get(k)() }}>{k}</PopoverLinkMenuItem>
    ))
  }

  if (!user) { return <></> } // guard

  return <div ref={hoverRef} className={styles.row}>
    <Link to={urls.users.show(user.id)} key={user.id} className={cx(styles.userListing, isHovered && styles.hover)}>
      <ProfileImg user={user} />
      <span>{user.name}</span>
    </Link>
    {interactive && <div
      ref={triggerRef}
      onClick={() => setShow(!show)}
      className={styles.badge}
    >
      {inflight
        ? <CircleSpinner size={20} />
        : (selected === 'following' && friendBadge)
          ? <Badge bg='#FA6400' outline>
            <Msg capitalized m={follow.level === 'friend' ? commonNouns.friends : messages.nFollowing} values={{ count: 1 }} />
          </Badge>
          : <Icon
            i='ios-more'
            size={25}
            style={{ display: !(show || isHovered) && 'none' }}
            className={styles.icon}
          />}
    </div>}
    {show && <PopoverLinkMenu
      onRequestClose={() => setShow(false)}
      placement='bottom'
      target={triggerRef}
      containerPadding={15}
    >
      {actions}
    </PopoverLinkMenu>
    }
  </div>
}

const mapStateToProps = (state, ownProps) => {
  const { currentUser } = state

  return {
    currentUser,
    findUser: uid => userSelectors.find(state, uid)
  }
}

const mapDispatchToProps = dispatch => ({
  followUser: (userId, level = 'follow') => dispatch(followActions.create({ itemId: userId, itemType: 'User', level })),
  updateFollow: (followId, level) => dispatch(followActions.update(followId, { follow: { level } })),
  unfollowUser: followId => dispatch(followActions.delete(followId))
})

export const MiniUser = connect(
  mapStateToProps,
  mapDispatchToProps
)(_MiniUser)

FollowsCard.propTypes = {
  user: p.object.isRequired,
  currentUser: p.object.isRequired,
  followers: p.instanceOf(Immutable.Iterable).isRequired,
  following: p.instanceOf(Immutable.Iterable).isRequired,
  defaultTo: p.string
}
